import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LayoutGrid, Table, MessageSquare, Check, ArrowLeft, AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface BidData {
  messageId: string;
  threadId: string;
  carrierId: string;
  carrierFirstName: string | null;
  carrierLastName: string | null;
  carrierCompany: string | null;
  carrierUserType: string;
  carrierRating: number | null;
  carrierCompletedShipments: number;
  isPreferredPartner: boolean;
  quotePrice: number | null;
  quoteTransitTime: string | null;
  quotePickupDate: string | null;
  hasMessage: boolean;
  submittedAt: string;
  status: string;
  threadPhase: string | null;
}

interface LoadBidsResponse {
  load: {
    id: string;
    origin: string;
    destination: string;
    weight: number;
    pickupDate: string;
  };
  bids: BidData[];
}

export default function BidComparison() {
  const { loadId } = useParams<{ loadId: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [acceptTarget, setAcceptTarget] = useState<BidData | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalPrice, setFinalPrice] = useState<string>('');

  const { data, isLoading, error } = useQuery<LoadBidsResponse>({
    queryKey: [`/api/loads/${loadId}/bids`],
  });

  const acceptMutation = useMutation({
    mutationFn: ({ messageId, finalPrice }: { messageId: string; finalPrice: number }) =>
      apiRequest("POST", `/api/messages/${messageId}/accept-quote`, { finalPrice }),
    onSuccess: () => {
      toast({ title: "Bid accepted", description: "Bid accepted — the load is now booked" });
      setConfirmOpen(false);
      setAcceptTarget(null);
      setFinalPrice('');
      queryClient.invalidateQueries({ queryKey: [`/api/loads/${loadId}/bids`] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message ?? "Failed to accept bid.", variant: "destructive" });
      setConfirmOpen(false);
    },
  });

  function handleAcceptClick(bid: BidData) {
    setAcceptTarget(bid);
    // Pre-fill with the quoted price converted from cents to dollars
    const dollars = bid.quotePrice ? (bid.quotePrice / 100).toFixed(0) : '';
    setFinalPrice(dollars);
    setConfirmOpen(true);
  }

  function handleConfirmAccept() {
    if (acceptTarget) {
      acceptMutation.mutate({
        messageId: acceptTarget.messageId,
        finalPrice: Number(finalPrice),
      });
    }
  }

  function handleDialogClose() {
    setConfirmOpen(false);
    setAcceptTarget(null);
    setFinalPrice('');
  }

  function handleMessageClick(bid: BidData) {
    setLocation(`/shipments?load=${loadId}&user=${bid.carrierId}`);
  }

  function formatPrice(cents: number | null): string {
    if (!cents) return "—";
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  function getInitials(firstName: string | null, lastName: string | null, company: string | null): string {
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (company) return company[0].toUpperCase();
    return "?";
  }

  // Determine if the edited final price deviates >20% from the original quoted price
  const finalPriceWarning = (() => {
    if (!acceptTarget?.quotePrice || !finalPrice || isNaN(Number(finalPrice))) return false;
    const originalDollars = acceptTarget.quotePrice / 100;
    const edited = Number(finalPrice);
    if (edited <= 0) return false;
    return Math.abs(edited - originalDollars) / originalDollars > 0.2;
  })();

  const lowestPrice = data?.bids.length
    ? Math.min(...data.bids.filter(b => b.quotePrice).map(b => b.quotePrice!))
    : null;

  if (isLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="h-8 w-48 bg-muted animate-pulse rounded mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <button
          onClick={() => setLocation("/dashboard")}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </button>
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-2">Unable to load bids for this shipment.</p>
          <p className="text-sm text-muted-foreground">Make sure you're logged in as the shipper who posted this load.</p>
        </Card>
      </div>
    );
  }

  const { load, bids } = data;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <button
            onClick={() => setLocation("/dashboard")}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-2"
            data-testid="button-back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </button>
          <h1 className="text-xl font-semibold">{load.origin} → {load.destination}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {load.weight?.toLocaleString()} lbs · Pickup {new Date(load.pickupDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} ·{" "}
            <strong className="text-foreground">{bids.length} bid{bids.length !== 1 ? "s" : ""} received</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "cards" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("cards")}
            data-testid="button-view-cards"
          >
            <LayoutGrid className="w-4 h-4 mr-1" />Cards
          </Button>
          <Button
            variant={viewMode === "table" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("table")}
            data-testid="button-view-table"
          >
            <Table className="w-4 h-4 mr-1" />Table
          </Button>
        </div>
      </div>

      {bids.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">No bids received yet. Check back soon.</p>
        </Card>
      ) : viewMode === "cards" ? (
        // Card view
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bids.map(bid => (
            <div key={bid.messageId} style={{ opacity: bid.threadPhase === 'quote_declined' ? 0.5 : 1 }} className="relative">
            <Card className="p-5 flex flex-col gap-3 relative">
              {bid.threadPhase === 'quote_declined' && (
                <div className="absolute top-2 right-2 z-10">
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    Not selected
                  </span>
                </div>
              )}
              {bid.quotePrice === lowestPrice && lowestPrice !== null && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-green-100 text-green-800 border-green-200 text-xs whitespace-nowrap">Lowest bid</Badge>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-medium text-sm text-blue-700 flex-shrink-0">
                  {getInitials(bid.carrierFirstName, bid.carrierLastName, bid.carrierCompany)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {bid.carrierFirstName} {bid.carrierLastName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {bid.carrierCompany} · {bid.isPreferredPartner ? "Your partner" : "Public board"}
                  </p>
                </div>
              </div>

              <div className="text-center py-3 border-t border-b">
                <p className="text-3xl font-semibold">{formatPrice(bid.quotePrice)}</p>
              </div>

              <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
                {bid.quoteTransitTime && (
                  <div className="flex justify-between">
                    <span>Transit time</span>
                    <span className="font-medium text-foreground">{bid.quoteTransitTime}</span>
                  </div>
                )}
                {bid.quotePickupDate && (
                  <div className="flex justify-between">
                    <span>Pickup available</span>
                    <span className="font-medium text-foreground">
                      {new Date(bid.quotePickupDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                )}
                {bid.carrierRating !== null && bid.carrierRating > 0 && bid.carrierCompletedShipments >= 3 && (
                  <div className="flex justify-between">
                    <span>Rating</span>
                    <span className="font-medium text-foreground">
                      {bid.carrierRating.toFixed(1)} · {bid.carrierCompletedShipments} shipments
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 mt-auto">
                <Button
                  className="w-full"
                  size="sm"
                  onClick={() => handleAcceptClick(bid)}
                  disabled={bid.status !== "pending" || bid.threadPhase === 'quote_declined'}
                  data-testid={`button-accept-${bid.carrierId}`}
                >
                  <Check className="w-4 h-4 mr-1" />
                  {bid.threadPhase === 'quote_declined' ? "Not selected" : bid.status === "pending" ? "Accept bid" : "Accepted"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => handleMessageClick(bid)}
                  data-testid={`button-message-${bid.carrierId}`}
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Message
                  {bid.hasMessage && (
                    <span className="ml-2 bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">1</span>
                  )}
                </Button>
              </div>
            </Card>
            </div>
          ))}
        </div>
      ) : (
        // Table view
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left p-3 font-medium text-muted-foreground">Carrier / Broker</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Bid</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Transit time</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Pickup available</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Rating</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Relationship</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bids.map((bid) => (
                <tr key={bid.messageId} style={{ opacity: bid.threadPhase === 'quote_declined' ? 0.5 : 1 }} className={`border-b last:border-0 ${bid.quotePrice === lowestPrice && lowestPrice !== null ? "bg-green-50/50" : ""}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-medium text-xs text-blue-700 flex-shrink-0">
                        {getInitials(bid.carrierFirstName, bid.carrierLastName, bid.carrierCompany)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{bid.carrierFirstName} {bid.carrierLastName}</p>
                        <p className="text-xs text-muted-foreground truncate">{bid.carrierCompany}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <span className={`font-semibold ${bid.quotePrice === lowestPrice && lowestPrice !== null ? "text-green-700" : ""}`}>
                      {formatPrice(bid.quotePrice)}
                    </span>
                    {bid.quotePrice === lowestPrice && lowestPrice !== null && (
                      <p className="text-xs text-green-600">lowest</p>
                    )}
                  </td>
                  <td className="p-3 text-center">{bid.quoteTransitTime ?? "—"}</td>
                  <td className="p-3 text-center">
                    {bid.quotePickupDate
                      ? new Date(bid.quotePickupDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                      : "—"}
                  </td>
                  <td className="p-3 text-center">
                    {bid.carrierRating && bid.carrierRating > 0 && bid.carrierCompletedShipments >= 3 ? `${bid.carrierRating.toFixed(1)} ★` : "—"}
                  </td>
                  <td className="p-3 text-center">
                    <Badge variant={bid.isPreferredPartner ? "secondary" : "outline"} className="text-xs whitespace-nowrap">
                      {bid.isPreferredPartner ? "Your partner" : "Public board"}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        className="text-xs px-3"
                        onClick={() => handleAcceptClick(bid)}
                        disabled={bid.status !== "pending" || bid.threadPhase === 'quote_declined'}
                        data-testid={`button-accept-row-${bid.carrierId}`}
                      >
                        {bid.threadPhase === 'quote_declined' ? "Not selected" : bid.status === "pending" ? "Accept" : "Accepted"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="relative px-3"
                        onClick={() => handleMessageClick(bid)}
                        data-testid={`button-message-row-${bid.carrierId}`}
                      >
                        <MessageSquare className="w-4 h-4" />
                        {bid.hasMessage && (
                          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full leading-none">1</span>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Accept confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!open) handleDialogClose(); }}>
        <DialogContent data-testid="dialog-accept-bid" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-700" />
            </div>
            <DialogTitle className="text-center">Accept this bid?</DialogTitle>
          </DialogHeader>
          {acceptTarget && (
            <>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Carrier</span>
                  <span className="font-medium">{acceptTarget.carrierFirstName} {acceptTarget.carrierLastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bid amount</span>
                  <span className="font-medium">{formatPrice(acceptTarget.quotePrice)}</span>
                </div>
                {acceptTarget.quoteTransitTime && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transit time</span>
                    <span className="font-medium">{acceptTarget.quoteTransitTime}</span>
                  </div>
                )}
                {acceptTarget.quotePickupDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pickup available</span>
                    <span className="font-medium">
                      {new Date(acceptTarget.quotePickupDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                )}
              </div>

              {/* Editable final price */}
              <div className="space-y-2">
                <Label htmlFor="final-price-input" className="text-sm font-medium">
                  Final Price ($)
                </Label>
                <Input
                  id="final-price-input"
                  type="number"
                  min="0"
                  step="1"
                  value={finalPrice}
                  onChange={(e) => setFinalPrice(e.target.value)}
                  placeholder="Enter final agreed price"
                  data-testid="input-final-price"
                />
                <p className="text-xs text-muted-foreground">
                  Pre-filled from the carrier's bid. Edit only if you've agreed on a different amount.
                </p>
                {finalPriceWarning && (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 px-3 py-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      This is significantly different from the original quote. Double-check the amount before confirming.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
          <DialogDescription className="text-center text-sm">
            Accepting this bid will immediately book the load and notify all other bidders that the load has been filled.
          </DialogDescription>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="w-full"
              onClick={handleConfirmAccept}
              disabled={acceptMutation.isPending || !finalPrice || Number(finalPrice) <= 0}
              data-testid="button-confirm-accept"
            >
              {acceptMutation.isPending ? "Accepting..." : "Confirm — accept bid"}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={handleDialogClose}
              data-testid="button-cancel-accept"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
